// src/components/eventos/components/diretor/EnviarMensagem.js
import React, { useState, useEffect, useRef } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    useToast,
} from '@chakra-ui/react';

const formatarHorario = (dataIso) => {
    if (!dataIso) return '';
    try {
        const d = new Date(dataIso);
        return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
};

const SendIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
);
const EyeIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
);
const EyeOffIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);
const HistoryIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-4.95" />
    </svg>
);
const TrashIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" />
    </svg>
);

const btnBase = {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '8px 16px', borderRadius: '9px', fontSize: '13px', fontWeight: 600,
    cursor: 'pointer', fontFamily: "'Inter', -apple-system, sans-serif", border: 'none', transition: 'all 0.15s',
};

const EnviarMensagem = ({ isOpen, onClose, evento, clienteId, supabase }) => {
    const [mensagem, setMensagem] = useState('');
    const [historico, setHistorico] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const toast = useToast();
    const intervalRef = useRef(null);

    const mensagemVisivel = historico.find(m => !m.lida) || null;

    const buscarMensagens = async () => {
        if (!evento || !clienteId) return;
        try {
            const { data, error } = await supabase
                .from('mensagens_evento').select('*')
                .eq('cliente_id', clienteId).eq('evento_id', evento.id)
                .order('created_at', { ascending: false });
            if (error) throw error;
            setHistorico(data || []);
        } catch (error) { console.error('Erro ao buscar mensagens:', error); }
    };

    useEffect(() => {
        if (!isOpen) return;
        buscarMensagens();
        intervalRef.current = setInterval(buscarMensagens, 3000);
        return () => clearInterval(intervalRef.current);
    }, [isOpen, evento, clienteId]); // eslint-disable-line react-hooks/exhaustive-deps

    const exibirMensagem = async () => {
        if (!mensagem.trim()) {
            toast({ title: 'Digite uma mensagem', status: 'warning', duration: 2000 });
            return;
        }
        setCarregando(true);
        try {
            if (mensagemVisivel) {
                await supabase.from('mensagens_evento').update({ lida: true }).eq('id', mensagemVisivel.id);
            }
            const { error } = await supabase.from('mensagens_evento').insert({
                cliente_id: clienteId, evento_id: evento.id, mensagem: mensagem.trim(), lida: false,
            });
            if (error) throw error;
            toast({ title: '✅ Mensagem exibida', description: 'O palestrante pode ver a mensagem agora', status: 'success', duration: 2000 });
            setMensagem('');
            await buscarMensagens();
        } catch (error) {
            toast({ title: 'Erro ao exibir mensagem', description: error.message, status: 'error', duration: 3000 });
        } finally { setCarregando(false); }
    };

    const ocultarMensagem = async () => {
        if (!mensagemVisivel) return;
        setCarregando(true);
        try {
            const { error } = await supabase.from('mensagens_evento').update({ lida: true }).eq('id', mensagemVisivel.id);
            if (error) throw error;
            toast({ title: '🔒 Mensagem ocultada', description: 'O palestrante não vê mais a mensagem', status: 'info', duration: 2000 });
            await buscarMensagens();
        } catch (error) {
            toast({ title: 'Erro ao ocultar mensagem', description: error.message, status: 'error', duration: 3000 });
        } finally { setCarregando(false); }
    };

    const reexibirMensagem = async (item) => {
        setCarregando(true);
        try {
            if (mensagemVisivel) {
                await supabase.from('mensagens_evento').update({ lida: true }).eq('id', mensagemVisivel.id);
            }
            const { error } = await supabase.from('mensagens_evento').update({ lida: false }).eq('id', item.id);
            if (error) throw error;
            toast({ title: '✅ Mensagem reexibida', status: 'success', duration: 2000 });
            await buscarMensagens();
        } catch (error) {
            toast({ title: 'Erro', description: error.message, status: 'error', duration: 3000 });
        } finally { setCarregando(false); }
    };

    const excluirMensagem = async (id) => {
        setCarregando(true);
        try {
            const { error } = await supabase.from('mensagens_evento').delete().eq('id', id);
            if (error) throw error;
            await buscarMensagens();
        } catch (error) {
            toast({ title: 'Erro ao excluir', description: error.message, status: 'error', duration: 3000 });
        } finally { setCarregando(false); }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
            <ModalOverlay bg="rgba(0,0,0,0.4)" backdropFilter="blur(8px)" />
            <ModalContent bg="#ffffff" border="1px solid #e2e8f0" borderRadius="20px" color="#1e293b" mx={4} fontFamily="'Inter', -apple-system, sans-serif" boxShadow="0 20px 60px rgba(0,0,0,0.15)">
                <ModalHeader pb={3} borderBottom="1px solid #e2e8f0">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ color: '#2563eb' }}><SendIcon /></span>
                        <span style={{ fontSize: '17px', fontWeight: 700, color: '#0f172a' }}>Mensagem ao Palestrante</span>
                    </div>
                </ModalHeader>
                <ModalCloseButton color="#94a3b8" />

                <ModalBody py={5} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Mensagem visível atual */}
                    {mensagemVisivel && (
                        <div style={{ padding: '12px 14px', background: 'rgba(44,203,144,0.06)', border: '1px solid rgba(44,203,144,0.2)', borderRadius: '10px' }}>
                            <p style={{ margin: '0 0 6px', fontSize: '10px', fontWeight: 700, color: '#2CCB90', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                👁 Mensagem visível para o palestrante
                            </p>
                            <p style={{ margin: 0, fontSize: '13px', color: '#1e293b', fontStyle: 'italic' }}>
                                "{mensagemVisivel.mensagem}"
                            </p>
                        </div>
                    )}

                    {/* Campo nova mensagem */}
                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                            Nova Mensagem
                        </label>
                        <textarea
                            value={mensagem}
                            onChange={e => setMensagem(e.target.value)}
                            placeholder="Digite a mensagem para o palestrante..."
                            rows={3}
                            style={{
                                width: '100%', background: '#f8fafc', border: '1px solid #e2e8f0',
                                borderRadius: '10px', color: '#1e293b', fontSize: '14px', padding: '10px 14px',
                                fontFamily: "'Inter', -apple-system, sans-serif", resize: 'vertical', outline: 'none',
                                boxSizing: 'border-box',
                            }}
                            onFocus={e => { e.target.style.borderColor = '#2CCB90'; e.target.style.background = '#f0fdf8'; }}
                            onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; }}
                        />
                    </div>

                    {/* Histórico */}
                    {historico.length > 0 && (
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '10px' }}>
                                <span style={{ color: '#94a3b8' }}><HistoryIcon /></span>
                                <span style={{ fontSize: '12px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Histórico ({historico.length})
                                </span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '200px', overflowY: 'auto' }}>
                                {historico.map(item => (
                                    <div key={item.id} style={{
                                        padding: '10px 12px', borderRadius: '8px',
                                        background: !item.lida ? 'rgba(44,203,144,0.05)' : '#f8fafc',
                                        border: `1px solid ${!item.lida ? 'rgba(44,203,144,0.2)' : '#e2e8f0'}`,
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{ fontSize: '11px', color: '#94a3b8' }}>{formatarHorario(item.created_at)}</span>
                                                {!item.lida && (
                                                    <span style={{ padding: '1px 7px', borderRadius: '5px', background: 'rgba(44,203,144,0.1)', border: '1px solid rgba(44,203,144,0.25)', color: '#2CCB90', fontSize: '10px', fontWeight: 700 }}>
                                                        visível
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', gap: '4px' }}>
                                                {item.lida && (
                                                    <button onClick={() => reexibirMensagem(item)} disabled={carregando}
                                                        style={{ ...btnBase, padding: '4px 8px', fontSize: '11px', background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.15)', color: '#2563eb' }}>
                                                        <EyeIcon /> Reexibir
                                                    </button>
                                                )}
                                                <button onClick={() => excluirMensagem(item.id)} disabled={carregando}
                                                    style={{ ...btnBase, padding: '4px 7px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', color: '#ef4444' }}>
                                                    <TrashIcon />
                                                </button>
                                            </div>
                                        </div>
                                        <p style={{ margin: 0, fontSize: '13px', color: '#1e293b' }}>{item.mensagem}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </ModalBody>

                <ModalFooter borderTop="1px solid #e2e8f0" gap={3}>
                    <button onClick={onClose} style={{ ...btnBase, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b' }}>
                        Fechar
                    </button>
                    {mensagemVisivel && (
                        <button onClick={ocultarMensagem} disabled={carregando}
                            style={{ ...btnBase, background: 'rgba(251,146,60,0.08)', border: '1px solid rgba(251,146,60,0.2)', color: '#ea580c' }}>
                            <EyeOffIcon /> Ocultar
                        </button>
                    )}
                    <button onClick={exibirMensagem} disabled={carregando || !mensagem.trim()}
                        style={{ ...btnBase, background: !mensagem.trim() ? 'rgba(44,203,144,0.06)' : 'linear-gradient(135deg, #2CCB90, #22a876)', border: 'none', color: !mensagem.trim() ? 'rgba(44,203,144,0.4)' : '#fff', cursor: !mensagem.trim() ? 'not-allowed' : 'pointer', boxShadow: mensagem.trim() ? '0 4px 14px rgba(44,203,144,0.25)' : 'none' }}>
                        <EyeIcon /> Exibir
                    </button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default EnviarMensagem;
